/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { SortableFields } from 'embercom/lib/suggestions/constants';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import Service, { inject as service } from '@ember/service';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { tracked } from '@glimmer/tracking';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EntityType } from 'embercom/models/data/entity-types';
import { REVIEWABLE_CONTENT_ENTITIES } from 'embercom/lib/ai-content-library/constants';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import { ProjectedConversationsRanking } from 'embercom/lib/suggestions/constants';
import type IntlService from 'embercom/services/intl';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { fetchTaskSuggestions } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/task-suggestions/task-suggestions-data-resources';
import type Store from '@ember-data/store';
import type ReportingChartService from './reporting-chart-service';
import type { TaskSuggestionStatus } from 'embercom/models/content-service/task-suggestion';

export type OptimizeSuggestionItem = {
  entityId: string;
  contentType?: EntityType;
  suggestionType: 'content-gap' | 'data-gap' | 'action-gap';
  title: string;
  description: string;
  createdAt: string;
  conversationIds: string[];
  suggestionState: Status | TaskSuggestionStatus;
  topicList: string[];
  subtopicList: string[];
  ranking: string;
  suggestion: ContentWrapper | TaskSuggestion;
};

export type OptimizeSuggestionList = OptimizeSuggestionItem[];

export default class OptimizeSuggestionsService extends Service {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare reportingChartService: ReportingChartService;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked declare suggestions: OptimizeSuggestionList | null[];

  @restartableTask
  *loadSuggestions(): TaskGenerator<void> {
    let promises: Promise<any>[] = [];
    promises.push(this.contentAPI());
    promises.push(this.loadTaskSuggestions());
    let [api, taskSuggestionsResponse] = yield Promise.all(promises);
    let contentSuggestions: OptimizeSuggestionList | null[] = [];
    let taskSuggestions: OptimizeSuggestionList | null[] = [];
    if (!api.isLoadingPage) {
      contentSuggestions = this.compileContentSuggestions(api.contentWrappers) as
        | OptimizeSuggestionList
        | null[];
    }
    if (taskSuggestionsResponse.length > 0) {
      taskSuggestions = this.compileTaskSuggestions(taskSuggestionsResponse) as
        | OptimizeSuggestionList
        | null[];
    }
    this.suggestions = [...contentSuggestions, ...taskSuggestions] as OptimizeSuggestionList;
  }

  findSuggestionByEntityId(entityId: string) {
    return this.suggestions.find((suggestion) => suggestion?.entityId === entityId);
  }

  private async contentAPI() {
    let sort_by = SortableFields.Priority;

    let api = new AiContentLibraryApi(getOwner(this), {
      object_types: this.reviewableContentEntities(),
      content_wrapper_types: [ContentWrapperType.AI_CONTENT],
      app_id: this.appService.app.id as string,
      per_page: 100,
      content_review_statuses: [Status.Pending],
      sort_by,
      sort_direction: 'desc',
    });

    let promises: Promise<any>[] = [api.loadPage()];
    promises.push(this.knowledgeHubService.fetchAvailableLocalesFromMessengerAndHelpCenter());
    await Promise.all(promises);

    this.contentSuggestionsService.listApi = api;

    return api;
  }

  private async loadTaskSuggestions() {
    let taskSuggestions = await fetchTaskSuggestions(
      this.store,
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
    );
    return taskSuggestions;
  }

  private compileTaskSuggestions(taskSuggestions: TaskSuggestion[]) {
    return taskSuggestions.map((taskSuggestion: TaskSuggestion) => {
      let id = taskSuggestion.id || Math.floor(Math.random() * 1000000).toString();
      let createdAt: string;
      if (taskSuggestion.createdAt) {
        if (typeof taskSuggestion.createdAt === 'string') {
          createdAt = taskSuggestion.createdAt;
        } else {
          createdAt = taskSuggestion.createdAt.toISOString();
        }
      } else {
        createdAt = new Date().toISOString();
      }
      return {
        entityId: id,
        contentType: undefined,
        suggestionType: this.taskSuggestionType(taskSuggestion),
        title: this.intl.t('ai-agent.suggestions.list-item.set-up-automation'),
        description: taskSuggestion.actionName,
        createdAt,
        conversationIds: taskSuggestion.sampledConversationIds,
        suggestionState: taskSuggestion.status,
        topicList: taskSuggestion.topicNameList,
        subtopicList: taskSuggestion.subtopics,
        ranking: this.intl.t('ai-agent.suggestions.list-item.projected-conversations-ranking.high'),
        suggestion: taskSuggestion,
      };
    });
  }

  private compileContentSuggestions(contentWrappers: ContentWrapper[]) {
    return contentWrappers.map((contentWrapper: ContentWrapper) => {
      let content = contentWrapper.contents.firstObject;
      if (!content || !contentWrapper.entityId) {
        return null;
      }

      return {
        entityId: contentWrapper.entityId.toString(),
        contentType: content.contentType,
        suggestionType: 'content-gap',
        title: this.contentTitle(content.contentReviewType, content.contentType),
        description: contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation,
        createdAt: content.contentData.most_recent_content_review_request_created_at,
        conversationIds: contentWrapper.contentData.suggestion_metadata.included_conversations,
        suggestionState: content.contentData.status,
        topicList: [content.contentData.suggestion_metadata.topic],
        subtopicList: content.contentData.suggestion_metadata.subtopics,
        ranking: this.projectedConversationsRanking(content.contentData),
        suggestion: contentWrapper,
      };
    });
  }

  private projectedConversationsRanking(contentData: any): string {
    let projectedConversationsRanking = contentData?.projected_conversations_ranking;

    switch (projectedConversationsRanking) {
      case ProjectedConversationsRanking.High:
        return this.intl.t('ai-agent.suggestions.list-item.projected-conversations-ranking.high');
      case ProjectedConversationsRanking.Medium:
        return this.intl.t('ai-agent.suggestions.list-item.projected-conversations-ranking.medium');
      case ProjectedConversationsRanking.Low:
        return this.intl.t('ai-agent.suggestions.list-item.projected-conversations-ranking.low');
      default:
        return this.intl.t('ai-agent.suggestions.list-item.projected-conversations-ranking.low');
    }
  }

  private taskSuggestionType(taskSuggestion: TaskSuggestion) {
    return taskSuggestion.isDataGap ? 'data-gap' : 'action-gap';
  }

  private contentTitle(contentReviewType: ReviewType, contentType: EntityType) {
    let objectKey = contentType === EntityType.ContentSnippet ? 'snippet' : 'article';
    switch (contentReviewType) {
      case ReviewType.CREATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.add-content');
      case ReviewType.EDIT_CONTENT:
        //next 2 can also be snippet but i need to figure out how to handle that
        return this.intl.t(`ai-agent.suggestions.list-item.titles.edit-${objectKey}`);
      case ReviewType.DELETE_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.delete-${objectKey}`);
      case ReviewType.DUPLICATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.duplicate-content');
      case ReviewType.CONTRADICTING_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.contradicting-content');
      case ReviewType.UNDERPERFORMING_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`);
      default:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.fallback-${objectKey}`);
    }
  }

  private reviewableContentEntities(): EntityType[] {
    let reviewableContentEntities = [...REVIEWABLE_CONTENT_ENTITIES];
    reviewableContentEntities.push(EntityType.ArticleContent);
    reviewableContentEntities.push(EntityType.ContentSectionRelationship);
    return reviewableContentEntities;
  }
}

declare module '@ember/service' {
  interface Registry {
    optimizeSuggestionsService: OptimizeSuggestionsService;
    'optimize-suggestions-service': OptimizeSuggestionsService;
  }
}
